import React, { useState, useEffect, useCallback } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import {
    GetAdminThemes,
    SelectActiveTheme,
    SaveAdminTheme,
    DeleteAdminTheme,
    UploadCardImage,
    resolveMediaUrl
} from './../../../../utilities/BL/MemoryIndividual';

/**
 * Genera un arreglo base de 6 cartas vacías para el creador de temas.
 *
 * @returns {Array<Object>} 6 slots de cartas con id y pairId asignados.
 */
const createEmptyCards = () => [
    { id: 1, pairId: 1, name: 'Carta 1', imageUrl: '' },
    { id: 2, pairId: 2, name: 'Carta 2', imageUrl: '' },
    { id: 3, pairId: 3, name: 'Carta 3', imageUrl: '' },
    { id: 4, pairId: 4, name: 'Carta 4', imageUrl: '' },
    { id: 5, pairId: 5, name: 'Carta 5', imageUrl: '' },
    { id: 6, pairId: 6, name: 'Carta 6', imageUrl: '' }
];

/**
 * Modal para la gestión completa de temas visuales y cartas de Memorama Individual.
 * Admite activación de tema en 1 clic, subida y optimización SkiaSharp a WebP,
 * y validación estricta de 6 cartas y reverso.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Visibilidad del modal.
 * @param {Function} props.onClose - Callback para cerrar el modal.
 * @param {Function} [props.onThemeActivated] - Callback al activar un tema exitosamente.
 */
export function MemoryIndividualThemeModal({ isOpen, onClose, onThemeActivated }) {
    const { showMessage } = useFloatMessage();

    // Estados de catálogo
    const [themes, setThemes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isActivating, setIsActivating] = useState(null);

    // Vista activa dentro del modal: 'catalog' | 'editor'
    const [activeView, setActiveView] = useState('catalog');

    // Estado del formulario de edición / creación
    const [editingThemeId, setEditingThemeId] = useState(0);
    const [themeName, setThemeName] = useState('');
    const [cardBackImage, setCardBackImage] = useState('');
    const [cards, setCards] = useState(createEmptyCards());
    const [isThemeActiveFlag, setIsThemeActiveFlag] = useState(false);

    // Estados de carga de archivos
    const [uploadingSlot, setUploadingSlot] = useState(null); // null | number (0-5)
    const [uploadingBack, setUploadingBack] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    /**
     * Carga el catálogo de temas desde el backend.
     */
    const loadThemes = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await GetAdminThemes();
            if (res && res.success && Array.isArray(res.data)) {
                setThemes(res.data);
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res?.message || 'Error al cargar los temas del memorama.'
                });
            }
        } catch (error) {
            console.error("Error al cargar temas (BL):", error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al cargar catálogo de temas.'
            });
        } finally {
            setIsLoading(false);
        }
    }, [showMessage]);

    useEffect(() => {
        if (isOpen) {
            loadThemes();
            setActiveView('catalog');
            resetForm();
        }
    }, [isOpen, loadThemes]);

    const resetForm = () => {
        setEditingThemeId(0);
        setThemeName('');
        setCardBackImage('');
        setCards(createEmptyCards());
        setIsThemeActiveFlag(false);
        setUploadingSlot(null);
        setUploadingBack(false);
        setIsSaving(false);
    };

    if (!isOpen) return null;

    /**
     * Activa un tema instantáneamente en 1 clic.
     */
    const handleActivate = async (themeId) => {
        setIsActivating(themeId);
        try {
            const res = await SelectActiveTheme(themeId);
            if (res && res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: 'Tema activado en 1 clic como tema oficial del juego.'
                });
                await loadThemes();
                if (onThemeActivated) {
                    onThemeActivated(themeId);
                }
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res?.message || 'Error al activar tema.'
                });
            }
        } catch (error) {
            console.error("Error al activar tema:", error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al activar tema.'
            });
        } finally {
            setIsActivating(null);
        }
    };

    /**
     * Abre el editor con un tema nuevo.
     */
    const handleStartNew = () => {
        resetForm();
        setThemeName(`Tema Memorama ${new Date().toLocaleDateString('es-MX')}`);
        setActiveView('editor');
    };

    /**
     * Abre el editor para modificar un tema existente.
     */
    const handleStartEdit = (theme) => {
        setEditingThemeId(theme.id);
        setThemeName(theme.name || '');
        setCardBackImage(theme.cardBackImage || '');
        setIsThemeActiveFlag(Boolean(theme.isActive));

        // Obtener arreglo de cartas desde el objeto o desde cardsJson
        let loadedCards = [];
        if (Array.isArray(theme.cards) && theme.cards.length > 0) {
            loadedCards = theme.cards;
        } else if (theme.cardsJson) {
            try {
                loadedCards = JSON.parse(theme.cardsJson);
            } catch (e) {
                loadedCards = [];
            }
        }

        const normalizedCards = createEmptyCards().map((slot, idx) => {
            const existing = loadedCards[idx];
            return existing
                ? { ...slot, imageUrl: existing.imageUrl || '', name: existing.name || `Carta ${idx + 1}` }
                : slot;
        });

        setCards(normalizedCards);
        setActiveView('editor');
    };

    /**
     * Elimina un tema que no esté activo.
     */
    const handleDelete = async (theme) => {
        if (theme.isActive) {
            showMessage({
                type: MESSAGE_TYPE.WARNING,
                text: 'No es posible eliminar el tema actualmente activo en la dinámica.'
            });
            return;
        }

        const confirmDelete = window.confirm(`¿Estás seguro de que deseas eliminar permanentemente el tema "${theme.name}"?`);
        if (!confirmDelete) return;

        try {
            const res = await DeleteAdminTheme(theme.id);
            if (res && res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: 'Tema eliminado correctamente.'
                });
                loadThemes();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res?.message || 'Error al eliminar tema.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al eliminar tema.'
            });
        }
    };

    /**
     * Sube y procesa una imagen para una de las 6 cartas con SkiaSharp.
     */
    const handleUploadCardSlot = async (file, slotIndex) => {
        if (!file) return;
        setUploadingSlot(slotIndex);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await UploadCardImage(formData);
            if (res && res.success && res.imageUrl) {
                setCards(prev => {
                    const next = [...prev];
                    next[slotIndex] = {
                        ...next[slotIndex],
                        imageUrl: res.imageUrl
                    };
                    return next;
                });
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Carta ${slotIndex + 1} optimizada con éxito (WebP).`
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res?.message || 'Error al optimizar imagen con SkiaSharp.'
                });
            }
        } catch (error) {
            console.error("Error al subir carta:", error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al subir carta.'
            });
        } finally {
            setUploadingSlot(null);
        }
    };

    /**
     * Sube y procesa la imagen para el reverso de carta.
     */
    const handleUploadCardBack = async (file) => {
        if (!file) return;
        setUploadingBack(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await UploadCardImage(formData);
            if (res && res.success && res.imageUrl) {
                setCardBackImage(res.imageUrl);
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: 'Reverso de carta optimizado con éxito (WebP).'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res?.message || 'Error al optimizar reverso.'
                });
            }
        } catch (error) {
            console.error("Error al subir reverso:", error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al subir reverso.'
            });
        } finally {
            setUploadingBack(false);
        }
    };

    /**
     * Valida y guarda el tema en el backend.
     */
    const handleSaveTheme = async (e) => {
        if (e) e.preventDefault();

        const trimmedName = themeName.trim();
        if (!trimmedName) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Por favor asigna un nombre para el tema visual.'
            });
            return;
        }

        if (!cardBackImage) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Debes subir la imagen del reverso de la carta (CardBackImage).'
            });
            return;
        }

        const missingCardSlot = cards.findIndex(c => !c.imageUrl || !c.imageUrl.trim());
        if (missingCardSlot !== -1) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: `Falta la imagen de la Carta ${missingCardSlot + 1}. El memorama requiere exactamente 6 cartas.`
            });
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                id: editingThemeId || 0,
                name: trimmedName,
                cardBackImage: cardBackImage.trim(),
                cards: cards.map((c, idx) => ({
                    id: idx + 1,
                    pairId: idx + 1,
                    imageUrl: c.imageUrl.trim(),
                    name: c.name || `Carta ${idx + 1}`
                })),
                isActive: isThemeActiveFlag
            };

            const res = await SaveAdminTheme(payload);
            if (res && res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: editingThemeId ? 'Tema visual actualizado con éxito.' : 'Nuevo tema creado con éxito.'
                });
                await loadThemes();
                setActiveView('catalog');
                resetForm();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res?.message || 'Error al guardar el tema visual.'
                });
            }
        } catch (error) {
            console.error("Error al guardar tema:", error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión con el servidor al guardar tema.'
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Auxiliar para extraer lista de cartas de un tema en catálogo
    const getThemeCardsList = (theme) => {
        if (Array.isArray(theme.cards) && theme.cards.length > 0) {
            return theme.cards;
        }
        if (theme.cardsJson) {
            try {
                return JSON.parse(theme.cardsJson);
            } catch (e) {
                return [];
            }
        }
        return [];
    };

    return (
        <div
            className="modal fade show d-block MemoryIndividualThemeModal"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            aria-labelledby="themeModalTitle"
            style={{
                backgroundColor: 'rgba(15, 23, 42, 0.75)',
                backdropFilter: 'blur(5px)',
                zIndex: 1060
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget && !isSaving && uploadingSlot === null && !uploadingBack) {
                    onClose();
                }
            }}
        >
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content shadow-lg border-0 rounded-4 overflow-hidden">
                    {/* Header */}
                    <div className="modal-header bg-dark text-white border-0 px-4 py-3">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-palette2 fs-4 text-warning"></i>
                            <div>
                                <h5 id="themeModalTitle" className="modal-title fw-bold mb-0">
                                    Gestión de Temas y Cartas — Memorama Individual
                                </h5>
                                <small className="text-white-50">
                                    Tile 51 • Optimización SkiaSharp a WebP (máx. 600px) • Selección en 1 clic
                                </small>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            aria-label="Cerrar"
                            disabled={isSaving}
                            onClick={onClose}
                        ></button>
                    </div>

                    {/* Sub-header con Tabs de navegación */}
                    <div className="bg-light border-bottom px-4 py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <ul className="nav nav-pills gap-2">
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-semibold px-3 py-1.5 rounded-pill ${
                                        activeView === 'catalog' ? 'active bg-primary' : 'text-secondary'
                                    }`}
                                    onClick={() => setActiveView('catalog')}
                                >
                                    <i className="bi bi-grid-3x3-gap-fill me-1.5"></i>
                                    Catálogo de Temas ({themes.length})
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-semibold px-3 py-1.5 rounded-pill ${
                                        activeView === 'editor' ? 'active bg-primary' : 'text-secondary'
                                    }`}
                                    onClick={() => {
                                        if (activeView !== 'editor') handleStartNew();
                                    }}
                                >
                                    <i className="bi bi-pencil-square me-1.5"></i>
                                    {editingThemeId ? `Editar Tema (#${editingThemeId})` : 'Crear Nuevo Tema'}
                                </button>
                            </li>
                        </ul>

                        {activeView === 'catalog' && (
                            <button
                                type="button"
                                className="btn btn-primary btn-sm fw-bold px-3 py-1.5 rounded-pill shadow-sm d-flex align-items-center gap-1.5"
                                onClick={handleStartNew}
                            >
                                <i className="bi bi-plus-circle-fill"></i>
                                <span>Nuevo Tema</span>
                            </button>
                        )}
                    </div>

                    {/* Modal Body */}
                    <div className="modal-body p-4 bg-light">
                        {/* ========================================== */}
                        {/* VISTA 1: CATÁLOGO DE TEMAS */}
                        {/* ========================================== */}
                        {activeView === 'catalog' && (
                            <>
                                {isLoading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Cargando temas...</span>
                                        </div>
                                        <p className="text-muted mt-2 mb-0">Cargando catálogo de temas...</p>
                                    </div>
                                ) : themes.length === 0 ? (
                                    <div className="text-center py-5 bg-white rounded-4 border p-5">
                                        <i className="bi bi-images fs-1 text-muted d-block mb-3"></i>
                                        <h5 className="fw-bold text-dark mb-2">No hay temas configurados</h5>
                                        <p className="text-secondary small mb-3">
                                            Crea el primer tema con las 6 cartas y el reverso oficial del Atlético de San Luis.
                                        </p>
                                        <button
                                            type="button"
                                            className="btn btn-primary fw-bold px-4 rounded-pill"
                                            onClick={handleStartNew}
                                        >
                                            <i className="bi bi-plus-lg me-1"></i> Crear Primer Tema
                                        </button>
                                    </div>
                                ) : (
                                    <div className="row g-4">
                                        {themes.map((theme) => {
                                            const themeCards = getThemeCardsList(theme);
                                            const isActive = Boolean(theme.isActive);

                                            return (
                                                <div key={theme.id} className="col-12 col-md-6 col-xl-4">
                                                    <div
                                                        className={`card h-100 border-2 rounded-4 shadow-sm transition-all overflow-hidden bg-white ${
                                                            isActive ? 'border-primary ring-2 ring-primary' : 'border-light-subtle'
                                                        }`}
                                                    >
                                                        {/* Header de la tarjeta */}
                                                        <div className="card-header bg-white border-0 pb-0 pt-3 px-3 d-flex justify-content-between align-items-start">
                                                            <div>
                                                                <h6 className="fw-bold text-dark mb-0 text-truncate" title={theme.name}>
                                                                    {theme.name}
                                                                </h6>
                                                                <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                                    ID #{theme.id} • 6 cartas + reverso
                                                                </small>
                                                            </div>
                                                            {isActive ? (
                                                                <span className="badge bg-primary text-white fw-bold px-2.5 py-1 rounded-pill d-flex align-items-center gap-1">
                                                                    <i className="bi bi-check-circle-fill"></i> Activo
                                                                </span>
                                                            ) : (
                                                                <span className="badge bg-secondary-subtle text-secondary fw-semibold px-2 py-1 rounded-pill">
                                                                    Inactivo
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* Miniaturas de cartas */}
                                                        <div className="card-body px-3 py-3">
                                                            <div className="d-flex align-items-center gap-2 mb-2">
                                                                {/* Reverso */}
                                                                <div
                                                                    className="border rounded-3 p-1 bg-dark text-center"
                                                                    style={{ width: '48px', height: '62px' }}
                                                                    title="Reverso de carta"
                                                                >
                                                                    {theme.cardBackImage ? (
                                                                        <img
                                                                            src={resolveMediaUrl(theme.cardBackImage)}
                                                                            alt="Reverso"
                                                                            className="w-100 h-100 object-fit-cover rounded-2"
                                                                        />
                                                                    ) : (
                                                                        <div className="w-100 h-100 d-flex align-items-center justify-content-center text-white-50">
                                                                            <i className="bi bi-card-image" style={{ fontSize: '1rem' }}></i>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Miniaturas de las 6 cartas en micro-grid */}
                                                                <div className="d-grid flex-grow-1" style={{ gridTemplateColumns: 'repeat(6, 1fr)', gap: '4px' }}>
                                                                    {themeCards.slice(0, 6).map((c, idx) => (
                                                                        <div
                                                                            key={c.id || idx}
                                                                            className="border rounded-2 p-0.5 bg-light text-center"
                                                                            style={{ height: '46px' }}
                                                                            title={c.name || `Carta ${idx + 1}`}
                                                                        >
                                                                            {c.imageUrl ? (
                                                                                <img
                                                                                    src={resolveMediaUrl(c.imageUrl)}
                                                                                    alt={c.name || `Carta ${idx + 1}`}
                                                                                    className="w-100 h-100 object-fit-cover rounded-1"
                                                                                />
                                                                            ) : (
                                                                                <span className="small text-muted">{idx + 1}</span>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                    {themeCards.length < 6 && (
                                                                        [...Array(6 - themeCards.length)].map((_, i) => (
                                                                            <div
                                                                                key={`empty-${i}`}
                                                                                className="border border-dashed rounded-2 p-0.5 bg-light text-center d-flex align-items-center justify-content-center"
                                                                                style={{ height: '46px' }}
                                                                            >
                                                                                <span className="small text-muted" style={{ fontSize: '0.65rem' }}>-</span>
                                                                            </div>
                                                                        ))
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Acciones del tema */}
                                                        <div className="card-footer bg-light border-0 pt-0 pb-3 px-3 d-flex justify-content-between align-items-center gap-2">
                                                            {isActive ? (
                                                                <span className="text-primary small fw-bold d-flex align-items-center gap-1">
                                                                    <i className="bi bi-broadcast"></i> En vivo en dinámica
                                                                </span>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-primary btn-sm fw-bold px-3 rounded-pill d-flex align-items-center gap-1.5"
                                                                    disabled={isActivating === theme.id}
                                                                    onClick={() => handleActivate(theme.id)}
                                                                >
                                                                    {isActivating === theme.id ? (
                                                                        <>
                                                                            <span className="spinner-border spinner-border-sm" role="status"></span>
                                                                            <span>Activando...</span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <i className="bi bi-lightning-charge-fill"></i>
                                                                            <span>Activar en 1 clic</span>
                                                                        </>
                                                                    )}
                                                                </button>
                                                            )}

                                                            <div className="d-flex align-items-center gap-1">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-light btn-sm text-secondary rounded-circle"
                                                                    title="Editar Tema"
                                                                    onClick={() => handleStartEdit(theme)}
                                                                    style={{ width: '32px', height: '32px' }}
                                                                >
                                                                    <i className="bi bi-pencil"></i>
                                                                </button>
                                                                {!isActive && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-light btn-sm text-danger rounded-circle"
                                                                        title="Eliminar Tema"
                                                                        onClick={() => handleDelete(theme)}
                                                                        style={{ width: '32px', height: '32px' }}
                                                                    >
                                                                        <i className="bi bi-trash"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}

                        {/* ========================================== */}
                        {/* VISTA 2: CREADOR / EDITOR DE TEMA */}
                        {/* ========================================== */}
                        {activeView === 'editor' && (
                            <form onSubmit={handleSaveTheme} id="memoryThemeForm" className="bg-white p-4 rounded-4 shadow-sm border">
                                <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                                    <div>
                                        <h5 className="fw-bold text-dark mb-0">
                                            {editingThemeId ? `Editar Tema (#${editingThemeId})` : 'Crear Nuevo Tema'}
                                        </h5>
                                        <small className="text-muted">
                                            Configura el nombre, el reverso y los 6 artes del memorama oficial.
                                        </small>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                                        onClick={() => setActiveView('catalog')}
                                    >
                                        <i className="bi bi-arrow-left me-1"></i> Volver al Catálogo
                                    </button>
                                </div>

                                {/* Nombre del Tema */}
                                <div className="mb-4">
                                    <label htmlFor="themeNameInput" className="form-label fw-bold text-dark small">
                                        Nombre del Tema <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        id="themeNameInput"
                                        type="text"
                                        className="form-control form-control-lg rounded-3"
                                        placeholder="Ej: Tema Estadio Alfonso Lastras / Mascotas / Histórico"
                                        value={themeName}
                                        onChange={(e) => setThemeName(e.target.value)}
                                        required
                                        maxLength={100}
                                        disabled={isSaving}
                                    />
                                </div>

                                {/* Slot 1: Reverso de Carta (CardBackImage) */}
                                <div className="card border-2 rounded-4 p-3 bg-light mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div>
                                            <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-1.5">
                                                <i className="bi bi-card-image text-primary"></i>
                                                Reverso Oficial de Cartas (CardBackImage) <span className="text-danger">*</span>
                                            </h6>
                                            <small className="text-muted">
                                                Se mostrará cuando las cartas estén boca abajo. Procesado con SkiaSharp a WebP (máx. 600px).
                                            </small>
                                        </div>
                                        {cardBackImage && (
                                            <button
                                                type="button"
                                                className="btn btn-link text-danger p-0 small text-decoration-none"
                                                onClick={() => setCardBackImage('')}
                                                disabled={uploadingBack || isSaving}
                                            >
                                                <i className="bi bi-x-circle me-1"></i>Remover
                                            </button>
                                        )}
                                    </div>

                                    <div className="d-flex align-items-center gap-3 flex-wrap">
                                        {/* Vista Previa */}
                                        <div
                                            className="border rounded-3 p-1 bg-white shadow-sm d-flex align-items-center justify-content-center overflow-hidden position-relative"
                                            style={{ width: '90px', height: '120px' }}
                                        >
                                            {uploadingBack ? (
                                                <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                                            ) : cardBackImage ? (
                                                <img
                                                    src={resolveMediaUrl(cardBackImage)}
                                                    alt="Reverso de Carta"
                                                    className="w-100 h-100 object-fit-cover rounded-2"
                                                />
                                            ) : (
                                                <div className="text-center text-muted p-1">
                                                    <i className="bi bi-cloud-arrow-up fs-3 d-block text-secondary"></i>
                                                    <span style={{ fontSize: '0.65rem' }}>Sin reverso</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Input de archivo */}
                                        <div className="flex-grow-1">
                                            <label
                                                htmlFor="cardBackFileInput"
                                                className="btn btn-outline-primary btn-sm fw-semibold rounded-pill px-3 py-1.5 cursor-pointer"
                                            >
                                                <i className="bi bi-upload me-1.5"></i>
                                                {cardBackImage ? 'Cambiar imagen de reverso' : 'Subir imagen de reverso'}
                                            </label>
                                            <input
                                                id="cardBackFileInput"
                                                type="file"
                                                className="d-none"
                                                accept="image/*"
                                                disabled={uploadingBack || isSaving}
                                                onChange={(e) => {
                                                    if (e.target.files?.[0]) handleUploadCardBack(e.target.files[0]);
                                                }}
                                            />
                                            <small className="text-secondary d-block mt-1">
                                                Recomendado: Imagen vertical (3:4 o 2:3), formato PNG o JPG, máx. 5MB.
                                            </small>
                                            {cardBackImage && (
                                                <small className="text-muted font-monospace text-truncate d-block mt-0.5" style={{ fontSize: '0.72rem' }}>
                                                    {cardBackImage}
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* 6 Slots de Cartas del Memorama */}
                                <div className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="fw-bold text-dark mb-0">
                                            Las 6 Cartas del Memorama <span className="text-danger">*</span>
                                        </h6>
                                        <small className="badge bg-primary-subtle text-primary fw-semibold px-2 py-1 rounded-pill">
                                            {cards.filter(c => Boolean(c.imageUrl)).length} de 6 cartas listas
                                        </small>
                                    </div>
                                    <p className="text-muted small mb-3">
                                        Cada arte representa un par idéntico que el aficionado deberá encontrar durante la partida.
                                    </p>

                                    <div className="row g-3">
                                        {cards.map((slot, index) => {
                                            const isSlotUploading = uploadingSlot === index;

                                            return (
                                                <div key={slot.id || index} className="col-6 col-md-4 col-lg-2">
                                                    <div className="card h-100 border rounded-3 p-2 bg-light text-center shadow-sm">
                                                        <span className="fw-bold small text-dark mb-1 d-block">
                                                            Carta #{index + 1}
                                                        </span>

                                                        {/* Slot Preview */}
                                                        <div
                                                            className="border rounded-2 bg-white mb-2 mx-auto d-flex align-items-center justify-content-center overflow-hidden position-relative"
                                                            style={{ width: '85px', height: '110px' }}
                                                        >
                                                            {isSlotUploading ? (
                                                                <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                                                            ) : slot.imageUrl ? (
                                                                <img
                                                                    src={resolveMediaUrl(slot.imageUrl)}
                                                                    alt={`Carta ${index + 1}`}
                                                                    className="w-100 h-100 object-fit-cover rounded-1"
                                                                />
                                                            ) : (
                                                                <div className="text-muted p-1">
                                                                    <i className="bi bi-image fs-3 d-block text-secondary"></i>
                                                                    <span style={{ fontSize: '0.65rem' }}>Vacía</span>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Botón de subida para el slot */}
                                                        <label
                                                            htmlFor={`cardSlotInput-${index}`}
                                                            className="btn btn-outline-secondary btn-sm fw-semibold rounded-pill px-2 py-1 mb-1 text-truncate w-100 cursor-pointer"
                                                            style={{ fontSize: '0.75rem' }}
                                                        >
                                                            <i className="bi bi-upload me-1"></i>
                                                            {slot.imageUrl ? 'Reemplazar' : 'Subir'}
                                                        </label>
                                                        <input
                                                            id={`cardSlotInput-${index}`}
                                                            type="file"
                                                            className="d-none"
                                                            accept="image/*"
                                                            disabled={isSlotUploading || isSaving}
                                                            onChange={(e) => {
                                                                if (e.target.files?.[0]) handleUploadCardSlot(e.target.files[0], index);
                                                            }}
                                                        />

                                                        {slot.imageUrl && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-link text-danger p-0 text-decoration-none"
                                                                style={{ fontSize: '0.72rem' }}
                                                                onClick={() => {
                                                                    setCards(prev => {
                                                                        const next = [...prev];
                                                                        next[index] = { ...next[index], imageUrl: '' };
                                                                        return next;
                                                                    });
                                                                }}
                                                                disabled={isSlotUploading || isSaving}
                                                            >
                                                                Limpiar
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Checkbox: Activar de inmediato al guardar */}
                                <div className="form-check form-switch mb-4">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="activateOnSaveSwitch"
                                        checked={isThemeActiveFlag}
                                        onChange={(e) => setIsThemeActiveFlag(e.target.checked)}
                                        disabled={isSaving}
                                    />
                                    <label className="form-check-label fw-semibold text-dark small" htmlFor="activateOnSaveSwitch">
                                        Establecer de inmediato como Tema Activo Oficial de la dinámica
                                    </label>
                                </div>

                                {/* Botones del formulario */}
                                <div className="d-flex justify-content-between align-items-center pt-3 border-top">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary px-4 fw-semibold rounded-pill"
                                        onClick={() => setActiveView('catalog')}
                                        disabled={isSaving}
                                    >
                                        Cancelar
                                    </button>

                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4 fw-bold rounded-pill shadow-sm d-flex align-items-center gap-2"
                                        disabled={
                                            isSaving ||
                                            uploadingBack ||
                                            uploadingSlot !== null ||
                                            !themeName.trim() ||
                                            !cardBackImage ||
                                            cards.some(c => !c.imageUrl)
                                        }
                                    >
                                        {isSaving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                                <span>Guardando...</span>
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-check-lg"></i>
                                                <span>Guardar Tema</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="modal-footer bg-white border-top px-4 py-3 d-flex justify-content-end">
                        <button
                            type="button"
                            className="btn btn-outline-secondary px-4 fw-semibold rounded-pill"
                            onClick={onClose}
                            disabled={isSaving}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MemoryIndividualThemeModal;
